import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop, faFilePdf, faPlayCircle, faUpload, faList, faHistory, faChartPie, faCheckCircle, faTimesCircle, faClock, faSpinner, faSync, faBalanceScale, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

const MESES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const fmtMonto = (n) => {
    if (n == null || n === '') return '—';
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);
};

const repararNombre = (str) => {
    if (!str || typeof str !== 'string') return str;
    if (/Ã./.test(str)) {
        try {
            const bytes = Uint8Array.from(str, (c) => c.charCodeAt(0));
            return new TextDecoder('utf-8').decode(bytes);
        } catch (_) { /* noop */ }
    }
    return str;
};

function TablaPropuestaSII({ propuesta }) {
    if (!propuesta?.lineas?.length) {
        return <p className="text-sm text-gray-500 italic py-2">Sin propuesta sincronizada para este período.</p>;
    }
    let seccionActual = '';
    return (
        <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
            {propuesta.cabecera?.texto && (
                <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 text-sm text-blue-900">
                    {propuesta.cabecera.texto}
                </div>
            )}
            <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                    <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Glosa</th>
                        <th className="px-3 py-2 text-center font-semibold text-gray-600 w-16">Cód.</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-600 w-32">Valor</th>
                    </tr>
                </thead>
                <tbody>
                    {propuesta.lineas.map((l, i) => {
                        const showSeccion = l.seccion && l.seccion !== seccionActual;
                        if (showSeccion) seccionActual = l.seccion;
                        return (
                            <React.Fragment key={i}>
                                {showSeccion && (
                                    <tr className="bg-blue-600 text-white">
                                        <td colSpan="3" className="px-3 py-1.5 font-bold text-xs uppercase tracking-wide">{l.seccion}</td>
                                    </tr>
                                )}
                                {l.subseccion && i > 0 && propuesta.lineas[i - 1]?.subseccion !== l.subseccion && (
                                    <tr className="bg-blue-100">
                                        <td colSpan="3" className="px-3 py-1 text-xs font-semibold text-blue-800">{l.subseccion}</td>
                                    </tr>
                                )}
                                <tr className={l.esTotal ? 'bg-blue-50 font-bold' : 'hover:bg-gray-50'}>
                                    <td className="px-3 py-1 text-gray-800">{l.operador && <span className="text-gray-400 mr-1">{l.operador}</span>}{l.glosa}</td>
                                    <td className="px-3 py-1 text-center text-gray-500">{l.codigo || '—'}</td>
                                    <td className="px-3 py-1 text-right text-gray-900">{l.valorTexto || (l.valor ? fmtMonto(l.valor) : '—')}</td>
                                </tr>
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function AgregarEmpresasRentaModal({ empresasF29, onClose, onAgregadas }) {
    const [rentaCompanies, setRentaCompanies] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const rutsF29 = new Set((empresasF29 || []).map(e => (e.rut || '').replace(/\./g, '').toUpperCase()));

    useEffect(() => {
        api.get('/listados/all')
            .then(res => {
                const renta = (res.data || []).filter(c => c.renta).map(c => ({
                    ...c,
                    nombre: repararNombre(c.nombre)
                }));
                setRentaCompanies(renta);
            })
            .catch(() => toast.error('Error al cargar listado Renta'))
            .finally(() => setLoading(false));
    }, []);

    const filtered = rentaCompanies.filter(c =>
        !search ||
        c.nombre.toLowerCase().includes(search.toLowerCase()) ||
        c.rut.includes(search)
    );

    const toggle = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAllVisible = () => {
        const ids = filtered.map(c => c._id);
        const allSelected = ids.every(id => selectedIds.has(id));
        setSelectedIds(prev => {
            const next = new Set(prev);
            ids.forEach(id => allSelected ? next.delete(id) : next.add(id));
            return next;
        });
    };

    const handleAgregar = async () => {
        if (!selectedIds.size) return toast.warning('Selecciona al menos una empresa');
        setSaving(true);
        try {
            const res = await api.post('/f29/agregar-desde-renta', {
                clienteIds: Array.from(selectedIds)
            });
            toast.success(res.data.message);
            onAgregadas();
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al agregar empresas');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="px-5 py-4 border-b flex items-center justify-between">
                    <div>
                        <h3 className="font-semibold text-gray-900">Agregar empresas</h3>
                        <p className="text-xs text-gray-500 mt-0.5">Selecciona una o más empresas del listado maestro</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
                </div>

                <div className="px-5 py-3 border-b">
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Buscar por nombre o RUT…"
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    />
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                        <span>{selectedIds.size} seleccionada(s)</span>
                        <button type="button" onClick={toggleAllVisible} className="text-blue-600 hover:underline">
                            {filtered.length > 0 && filtered.every(c => selectedIds.has(c._id)) ? 'Desmarcar visibles' : 'Marcar visibles'}
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto divide-y">
                    {loading ? (
                        <p className="text-center text-sm text-gray-400 py-10">Cargando empresas…</p>
                    ) : filtered.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-10">Sin resultados</p>
                    ) : filtered.map(c => {
                        const yaEnF29 = rutsF29.has((c.rut || '').replace(/\./g, '').toUpperCase());
                        const selected = selectedIds.has(c._id);
                        return (
                            <label key={c._id}
                                className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors ${selected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                                <input type="checkbox" checked={selected} onChange={() => toggle(c._id)}
                                    className="accent-blue-600 flex-shrink-0 w-4 h-4" />
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm text-gray-800 break-words leading-snug">{c.nombre}</div>
                                    <div className="text-xs text-gray-400 font-mono">{c.rut}</div>
                                </div>
                                {yaEnF29 && (
                                    <span className="text-[10px] uppercase font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex-shrink-0">
                                        En F29
                                    </span>
                                )}
                            </label>
                        );
                    })}
                </div>

                <div className="px-5 py-3 border-t bg-gray-50 flex gap-2">
                    <button onClick={handleAgregar} disabled={saving || !selectedIds.size}
                        className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 transition-colors">
                        {saving ? 'Agregando…' : `Agregar ${selectedIds.size || ''} empresa(s)`}
                    </button>
                    <button onClick={onClose}
                        className="px-4 py-2.5 border text-gray-600 hover:bg-white text-sm rounded-lg transition-colors">
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
}

function PanelF29Admin() {
    const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, lista, historial
    const [empresas, setEmpresas] = useState([]);
    const [logs, setLogs] = useState({});
    const [propuestas, setPropuestas] = useState({});
    const [cruces, setCruces] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedLogId, setExpandedLogId] = useState(null);
    const [selectedEmpresas, setSelectedEmpresas] = useState(new Set());
    
    // Selectores
    const [selectedMes, setSelectedMes] = useState(new Date().getMonth() || 12);
    const [selectedAnio, setSelectedAnio] = useState(selectedMes === 12 ? new Date().getFullYear() - 1 : new Date().getFullYear());
    const fileInputRef = useRef(null);

    const [isUploading, setIsUploading] = useState(false);
    const [isExecuting, setIsExecuting] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [showAgregarRenta, setShowAgregarRenta] = useState(false);

    useEffect(() => {
        cargarEmpresas();
    }, []);

    useEffect(() => {
        if (empresas.length > 0) {
            localStorage.setItem('selectedEmpresasF29', JSON.stringify(Array.from(selectedEmpresas)));
        }
    }, [selectedEmpresas, empresas]);

    useEffect(() => {
        cargarLogs();
        cargarPropuestas();
        cargarCruces();
        const haySync = Object.values(propuestas).some(p => p.estadoSync === 'en_progreso');
        const intervalo = haySync || isSyncing ? 2000 : 5000;
        const interval = setInterval(() => {
            cargarLogs();
            cargarPropuestas();
            cargarCruces();
        }, intervalo);
        return () => clearInterval(interval);
    }, [selectedMes, selectedAnio, isSyncing, propuestas]);

    const cargarEmpresas = async () => {
        try {
            const res = await api.get('/f29/listado');
            setEmpresas(res.data.map(e => ({ ...e, nombre: repararNombre(e.nombre) })));
            
            const saved = localStorage.getItem('selectedEmpresasF29');
            if (saved) {
                const parsed = JSON.parse(saved);
                const validIds = res.data.map(e => e._id);
                const filtered = parsed.filter(id => validIds.includes(id));
                setSelectedEmpresas(new Set(filtered));
            } else {
                setSelectedEmpresas(new Set(res.data.map(e => e._id)));
            }
        } catch (error) {
            toast.error('Error al cargar lista de empresas F29');
        }
    };

    const cargarLogs = async () => {
        if (!selectedMes || !selectedAnio) return;
        try {
            const res = await api.get(`/f29/logs/${selectedAnio}/${selectedMes}`);
            const logsMap = {};
            res.data.forEach(log => {
                logsMap[String(log.empresaId)] = log;
            });
            setLogs(logsMap);
        } catch (error) {
            console.error('Error cargando logs F29', error);
        }
    };

    const cargarPropuestas = async () => {
        if (!selectedMes || !selectedAnio) return;
        try {
            const res = await api.get(`/f29/propuestas/${selectedAnio}/${selectedMes}`);
            const map = {};
            res.data.forEach(p => { map[String(p.empresaId)] = p; });
            setPropuestas(map);
        } catch (error) {
            console.error('Error cargando propuestas F29', error);
        }
    };

    const cargarCruces = async () => {
        if (!selectedMes || !selectedAnio) return;
        try {
            const res = await api.get(`/f29/cruces/${selectedAnio}/${selectedMes}`);
            const map = {};
            res.data.forEach(c => { map[String(c.empresaId)] = c; });
            setCruces(map);
        } catch (error) {
            console.error('Error cargando cruces F29', error);
        }
    };

    const refrescarHistorial = () => {
        cargarLogs();
        cargarPropuestas();
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setIsUploading(true);
        try {
            const res = await api.post('/f29/upload-csv', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(res.data.message);
            cargarEmpresas();
            setActiveTab('lista');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al subir el archivo');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const ejecutarMasivo = async () => {
        if (empresas.length === 0) return toast.warning('No hay empresas cargadas.');
        if (selectedEmpresas.size === 0) return toast.warning('Selecciona al menos una empresa.');
        if (!window.confirm(`¿Estás seguro de iniciar la declaración automática para ${selectedEmpresas.size} empresas en el período ${MESES[selectedMes]} ${selectedAnio}?`)) return;

        setIsExecuting(true);
        try {
            const res = await api.post('/f29/ejecutar', {
                mes: selectedMes,
                anio: selectedAnio,
                empresasIds: Array.from(selectedEmpresas)
            });
            toast.success(res.data.message);
            cargarLogs();
        } catch (error) {
            toast.error('Error al iniciar la ejecución masiva');
        } finally {
            setIsExecuting(false);
        }
    };

    const toggleAllEmpresas = () => {
        if (selectedEmpresas.size === empresas.length) {
            setSelectedEmpresas(new Set());
        } else {
            setSelectedEmpresas(new Set(empresas.map(e => e._id)));
        }
    };

    const toggleEmpresa = (id, event) => {
        event.stopPropagation();
        const newSet = new Set(selectedEmpresas);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedEmpresas(newSet);
    };

    const detenerEjecucion = async () => {
        if (!window.confirm('¿Detener procesos en curso? Se cancelará la cola de declaración y sync de propuesta, y se liberarán empresas atascadas en "Sync".')) return;
        try {
            const [resDecl, resSync] = await Promise.all([
                api.post('/f29/detener'),
                api.post('/f29/detener-sync-propuesta')
            ]);
            toast.success(`${resDecl.data.message} ${resSync.data.message}`);
            cargarLogs();
            cargarPropuestas();
            cargarCruces();
            setIsExecuting(false);
            setIsSyncing(false);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al detener la ejecución');
        }
    };

    const eliminarEmpresa = async (empresaId, nombre) => {
        if (!window.confirm(`¿Eliminar "${nombre}" del listado F29?\nSe borrarán también sus logs y propuestas guardadas.`)) return;
        try {
            await api.delete(`/f29/empresa/${empresaId}`);
            toast.success('Empresa eliminada');
            setSelectedEmpresas(prev => {
                const next = new Set(prev);
                next.delete(empresaId);
                return next;
            });
            if (expandedLogId === empresaId) setExpandedLogId(null);
            cargarEmpresas();
            cargarLogs();
            cargarPropuestas();
            cargarCruces();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al eliminar empresa');
        }
    };

    const ejecutarIndividual = async (empresaId) => {
        try {
            await api.post('/f29/ejecutar', {
                empresasIds: [empresaId],
                mes: selectedMes,
                anio: selectedAnio
            });
            toast.success('Empresa encolada');
            cargarLogs();
        } catch (error) {
            toast.error('Error al encolar la empresa');
        }
    };

    const syncPropuestaMasivo = async () => {
        if (empresas.length === 0) return toast.warning('No hay empresas cargadas.');
        if (selectedEmpresas.size === 0) return toast.warning('Selecciona al menos una empresa.');
        if (!window.confirm(`¿Sincronizar propuesta SII para ${selectedEmpresas.size} empresas (${MESES[selectedMes]} ${selectedAnio})?`)) return;

        setIsSyncing(true);
        try {
            const res = await api.post('/f29/sync-propuesta', {
                mes: selectedMes,
                anio: selectedAnio,
                empresasIds: Array.from(selectedEmpresas)
            });
            toast.success(res.data.message);
            cargarPropuestas();
            cargarCruces();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al sincronizar propuestas');
        } finally {
            setIsSyncing(false);
        }
    };

    const syncPropuestaIndividual = async (empresaId) => {
        try {
            const res = await api.post('/f29/sync-propuesta', {
                mes: selectedMes,
                anio: selectedAnio,
                empresasIds: [empresaId]
            });
            toast.success(res.data.message || 'Sync iniciado');
            cargarPropuestas();
            cargarCruces();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al sincronizar propuesta');
        }
    };

    const toggleActivaDeclaracion = async (empresaId, valorActual, e) => {
        e.stopPropagation();
        try {
            const nuevo = !(valorActual !== false);
            const res = await api.patch(`/f29/empresa/${empresaId}/activa-declaracion`, {
                activaDeclaracion: nuevo
            });
            setEmpresas(prev => prev.map(emp => emp._id === empresaId ? { ...emp, activaDeclaracion: res.data.activaDeclaracion } : emp));
            toast.success(nuevo ? 'Incluida en cola día 8' : 'Excluida de cola día 8');
        } catch (error) {
            toast.error('Error al actualizar toggle');
        }
    };

    const getEstadoBadge = (log) => {
        if (!log || !log.estado) return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">Pendiente</span>;
        switch (log.estado) {
            case 'en_cola': return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800"><FontAwesomeIcon icon={faClock} className="mr-1" /> En Cola</span>;
            case 'en_progreso': return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800"><FontAwesomeIcon icon={faSpinner} spin className="mr-1" /> Procesando</span>;
            case 'completado': 
                if (log.resultado?.type === 'ya_declarado') {
                    return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 shadow-sm"><FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Ya Declarado</span>;
                }
                return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"><FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Completado</span>;
            case 'ya_declarado':
                return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 shadow-sm"><FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Ya Declarado</span>;
            case 'error': return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800"><FontAwesomeIcon icon={faTimesCircle} className="mr-1" /> Error</span>;
            default: return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">Pendiente</span>;
        }
    };

    const descargarPdf = (logId) => {
        const token = localStorage.getItem('token');
        const url = `${api.defaults.baseURL}/f29/descargar-pdf/${logId}?token=${token}`;
        window.open(url, '_blank');
    };

    const getPropuestaBadge = (prop) => {
        if (!prop || !prop.estadoSync || prop.estadoSync === 'sin_propuesta') {
            return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Sin sync</span>;
        }
        switch (prop.estadoSync) {
            case 'ok': return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">OK</span>;
            case 'en_progreso': return <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800"><FontAwesomeIcon icon={faSpinner} spin className="mr-1" />Sync</span>;
            case 'bloqueada': return <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800">Declarado</span>;
            case 'error': return <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800" title={prop.errorMensaje}>Error</span>;
            default: return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">{prop.estadoSync}</span>;
        }
    };

    const getCruceBadge = (cruce) => {
        if (!cruce) return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">—</span>;
        if (cruce.sinClienteContable) return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Sin cliente</span>;
        if (!cruce.tienePropuesta) return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">Sin propuesta</span>;
        if (cruce.cuadra) return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800"><FontAwesomeIcon icon={faCheckCircle} className="mr-1" />OK</span>;
        if (cruce.desajustes > 0) return <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-800">{cruce.desajustes} diff</span>;
        return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">Sin datos</span>;
    };

    const renderTerminal = (entries, vacio = 'Sin registros.') => (
        <div className="bg-slate-900 rounded-lg p-4 shadow-inner max-h-[280px] overflow-y-auto">
            {entries?.length > 0 ? (
                <div className="space-y-1 font-mono text-xs">
                    {entries.map((entry, i) => (
                        <div key={i} className={`${entry.includes('ERROR') || entry.includes('❌') ? 'text-red-400 font-bold' : entry.includes('✅') ? 'text-green-400' : 'text-gray-300'} leading-relaxed`}>{entry}</div>
                    ))}
                </div>
            ) : (
                <p className="text-slate-500 text-sm italic py-2">{vacio}</p>
            )}
        </div>
    );

    const buildHistorialItems = () => {
        const items = [];
        Object.values(logs).forEach(log => {
            items.push({ ...log, tipo: 'declaracion', empresaId: String(log.empresaId) });
        });
        Object.values(propuestas).forEach(p => {
            if (!p.estadoSync || p.estadoSync === 'sin_propuesta') return;
            const estadoMap = { ok: 'completado', error: 'error', en_progreso: 'en_progreso', bloqueada: 'ya_declarado' };
            items.push({
                _id: `prop-${p._id || p.empresaId}`,
                empresaId: String(p.empresaId),
                nombre: p.nombre,
                rut: p.rut,
                estado: estadoMap[p.estadoSync] || p.estadoSync,
                resultado: {
                    type: 'sync_propuesta',
                    mensaje: p.errorMensaje || (p.estadoSync === 'ok'
                        ? `Sync OK — ${p.lineas?.length || 0} líneas`
                        : p.estadoSync === 'bloqueada' ? 'Mes declarado — propuesta no actualizada' : p.estadoSync)
                },
                logEntries: p.logEntries || [],
                propuestaScreenshot: p.errorScreenshot,
                updatedAt: p.fechaActualizacion || p.updatedAt,
                tipo: 'propuesta'
            });
        });
        return items;
    };

    const getResumenEstadoFila = (log, prop) => {
        if (log?.estado === 'en_progreso') {
            return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Procesando</span>;
        }
        if (log?.estado === 'en_cola') {
            return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">En cola</span>;
        }
        if (log?.estado === 'error') {
            return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Error</span>;
        }
        if (log?.estado === 'completado' || log?.estado === 'ya_declarado') {
            return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Declarado</span>;
        }
        if (prop?.estadoSync === 'en_progreso') {
            return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Sync propuesta</span>;
        }
        if (prop?.estadoSync === 'error') {
            return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Error sync</span>;
        }
        if (prop?.estadoSync === 'ok') {
            return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">Propuesta OK</span>;
        }
        return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">Pendiente</span>;
    };

    // Calcular estadísticas para el dashboard
    const haySyncActivo = Object.values(propuestas).some(p => p.estadoSync === 'en_progreso');
    const syncEnCurso = isSyncing || haySyncActivo;
    const stats = { total: empresas.length, completados: 0, errores: 0, en_cola: 0, pendientes: 0 };
    empresas.forEach(emp => {
        const estado = logs[String(emp._id)]?.estado;
        if (estado === 'completado' || estado === 'ya_declarado') stats.completados++;
        else if (estado === 'error') stats.errores++;
        else if (estado === 'en_cola' || estado === 'en_progreso') stats.en_cola++;
        else stats.pendientes++;
    });

    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto bg-gray-50 min-h-screen">
            <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div>
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Módulo F29</h1>
                    <p className="text-gray-500 text-sm mt-1">Gestión mensual: propuesta SII, cruce contable y declaración automática</p>
                </div>
                
                {/* Selectores de Período Globales */}
                <div className="mt-4 md:mt-0 flex gap-3 items-center bg-gray-50 p-2 rounded-lg border border-gray-200">
                    <div>
                        <select 
                            value={selectedMes} 
                            onChange={(e) => setSelectedMes(Number(e.target.value))}
                            className="block w-full pl-3 pr-8 py-2 text-sm font-medium bg-white border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md shadow-sm"
                        >
                            {MESES.map((m, i) => i > 0 && <option key={i} value={i}>{m}</option>)}
                        </select>
                    </div>
                    <div>
                        <input 
                            type="number" 
                            value={selectedAnio} 
                            onChange={(e) => setSelectedAnio(Number(e.target.value))}
                            className="block w-20 pl-3 pr-3 py-2 text-sm font-medium bg-white border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md shadow-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Pestañas de Navegación */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('dashboard')}
                        className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                            activeTab === 'dashboard'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <FontAwesomeIcon icon={faChartPie} className="mr-2" />
                        Dashboard F29
                    </button>
                    <button
                        onClick={() => setActiveTab('historial')}
                        className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                            activeTab === 'historial'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <FontAwesomeIcon icon={faHistory} className="mr-2" />
                        Historial (Logs)
                    </button>
                </nav>
            </div>

            {/* TAB: DASHBOARD (Muestra Stats y la Lista de Empresas) */}
            {activeTab === 'dashboard' && (
                <div className="space-y-6">
                    {/* Estadísticas */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-500">Total Empresas</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                            </div>
                            <div className="p-3 bg-gray-100 rounded-full text-gray-600">
                                <FontAwesomeIcon icon={faList} size="lg" />
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-green-600">Completados</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.completados}</p>
                            </div>
                            <div className="p-3 bg-green-100 rounded-full text-green-600">
                                <FontAwesomeIcon icon={faCheckCircle} size="lg" />
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-red-600">Errores</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.errores}</p>
                            </div>
                            <div className="p-3 bg-red-100 rounded-full text-red-600">
                                <FontAwesomeIcon icon={faTimesCircle} size="lg" />
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-blue-600">En Progreso/Cola</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.en_cola}</p>
                            </div>
                            <div className="p-3 bg-blue-100 rounded-full text-blue-600">
                                <FontAwesomeIcon icon={faClock} size="lg" />
                            </div>
                        </div>
                    </div>

                    {/* Upload CSV y Acciones */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col gap-4">
                        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-bold text-gray-700 mb-2">Gestionar empresas F29</label>
                                <div className="flex flex-wrap gap-2">
                                    <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-100 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors text-sm font-medium text-gray-700">
                                        <FontAwesomeIcon icon={faUpload} className="text-gray-500" />
                                        Importar CSV
                                        <input 
                                            type="file" 
                                            accept=".csv"
                                            ref={fileInputRef}
                                            className="hidden"
                                            onChange={handleFileUpload}
                                            disabled={isUploading}
                                        />
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setShowAgregarRenta(true)}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                        Agregar empresas
                                    </button>
                                </div>
                                <p className="text-xs text-gray-400 mt-2">CSV reemplaza el listado completo. Agregar empresas incorpora o actualiza sin borrar las demás.</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                            <button 
                                onClick={syncPropuestaMasivo}
                                disabled={empresas.length === 0 || syncEnCurso}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-all w-full md:w-auto shadow-sm"
                            >
                                <FontAwesomeIcon icon={faSync} spin={syncEnCurso} />
                                Sync Propuesta SII
                            </button>
                            <button 
                                onClick={ejecutarMasivo}
                                disabled={empresas.length === 0}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-all w-full md:w-auto shadow-sm"
                            >
                                <FontAwesomeIcon icon={faPlayCircle} />
                                Declarar F29
                            </button>
                            <button 
                                onClick={detenerEjecucion}
                                disabled={!syncEnCurso && !isExecuting}
                                className="bg-red-100 hover:bg-red-200 text-red-700 px-5 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all w-full md:w-auto disabled:opacity-40"
                            >
                                <FontAwesomeIcon icon={faStop} />
                                Detener
                            </button>
                            </div>
                        </div>
                    </div>

                    {/* Tabla de Resultados */}
                    <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left w-10">
                                            <input 
                                                type="checkbox" 
                                                checked={empresas.length > 0 && selectedEmpresas.size === empresas.length}
                                                onChange={toggleAllEmpresas}
                                                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                            />
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Empresa</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-32">Estado</th>
                                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-20">Día 8</th>
                                        <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider min-w-[280px]">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {empresas
                                        .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
                                        .map(emp => {
                                        const log = logs[String(emp._id)];
                                        const prop = propuestas[String(emp._id)];
                                        const cruce = cruces[String(emp._id)];
                                        const activa = emp.activaDeclaracion !== false;
                                        const expandida = expandedLogId === emp._id;
                                        return (
                                            <React.Fragment key={emp._id}>
                                                <tr 
                                                    className={`transition-colors cursor-pointer ${log?.estado === 'en_progreso' ? 'bg-yellow-50/60 hover:bg-yellow-100/60' : expandida ? 'bg-blue-50/40' : 'hover:bg-blue-50'}`}
                                                    onClick={() => setExpandedLogId(expandida ? null : emp._id)}
                                                >
                                                    <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={selectedEmpresas.has(emp._id)}
                                                            onChange={(e) => toggleEmpresa(emp._id, e)}
                                                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 align-top">
                                                        <div className="flex items-start gap-2">
                                                            <span className="text-gray-400 text-xs mt-1 w-3 shrink-0">{expandida ? '▼' : '▶'}</span>
                                                            <div className="min-w-0">
                                                                <div className="text-sm font-medium text-gray-900 break-words leading-snug">{emp.nombre}</div>
                                                                <div className="text-xs text-gray-500 font-mono mt-0.5">{emp.rut}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 align-top whitespace-nowrap">
                                                        {getResumenEstadoFila(log, prop)}
                                                    </td>
                                                    <td className="px-4 py-3 align-top text-center" onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => toggleActivaDeclaracion(emp._id, emp.activaDeclaracion, e)}
                                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${activa ? 'bg-green-500' : 'bg-gray-300'}`}
                                                            title={activa ? 'Activa en cola día 8' : 'Excluida de cola día 8'}
                                                        >
                                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${activa ? 'translate-x-6' : 'translate-x-1'}`} />
                                                        </button>
                                                    </td>
                                                    <td className="px-4 py-3 align-top text-right" onClick={e => e.stopPropagation()}>
                                                        <div className="inline-flex flex-nowrap items-center justify-end gap-1.5">
                                                            <button
                                                                type="button"
                                                                onClick={() => syncPropuestaIndividual(emp._id)}
                                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 whitespace-nowrap"
                                                            >
                                                                <FontAwesomeIcon icon={faSync} spin={prop?.estadoSync === 'en_progreso'} />
                                                                Sync
                                                            </button>
                                                            {log?.resultado?.rutaPdf && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => descargarPdf(log._id)}
                                                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap"
                                                                >
                                                                    <FontAwesomeIcon icon={faFilePdf} />
                                                                    PDF
                                                                </button>
                                                            )}
                                                            {(!log || log.estado === 'error' || log.estado === 'completado') && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => ejecutarIndividual(emp._id)}
                                                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-green-600 text-white hover:bg-green-700 whitespace-nowrap"
                                                                >
                                                                    <FontAwesomeIcon icon={faPlay} />
                                                                    F29
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => eliminarEmpresa(emp._id, emp.nombre)}
                                                                disabled={prop?.estadoSync === 'en_progreso'}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 disabled:opacity-40"
                                                                title="Eliminar del listado F29"
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                {expandida && (
                                                    <tr>
                                                        <td colSpan="5" className="px-4 py-4 bg-gray-50 border-b border-gray-200">
                                                            <div className="space-y-4">
                                                                <div className="flex flex-wrap gap-4 text-sm bg-white border border-gray-200 rounded-lg p-3">
                                                                    <div><span className="text-xs text-gray-400 block">Propuesta</span>{getPropuestaBadge(prop)} {(prop?.montoAPagar != null) && <span className="text-xs font-semibold ml-1">91: {fmtMonto(prop.montoAPagar)}</span>}</div>
                                                                    <div><span className="text-xs text-gray-400 block">Declaración</span>{getEstadoBadge(log)}</div>
                                                                    <div><span className="text-xs text-gray-400 block">Cruce</span>{getCruceBadge(cruce)}</div>
                                                                </div>
                                                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                                                    <div>
                                                                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                                                            <FontAwesomeIcon icon={faList} /> Propuesta SII
                                                                            {prop?.fechaActualizacion && (
                                                                                <span className="font-normal text-gray-400 normal-case">
                                                                                    · {new Date(prop.fechaActualizacion).toLocaleString('es-CL')}
                                                                                </span>
                                                                            )}
                                                                        </h4>
                                                                        <TablaPropuestaSII propuesta={prop} />
                                                                    </div>
                                                                    <div className="space-y-4">
                                                                        <div>
                                                                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                                                                <FontAwesomeIcon icon={faBalanceScale} /> Cruce contable
                                                                                {cruce?.resumen && <span className="font-normal text-gray-400 normal-case">— {cruce.resumen}</span>}
                                                                            </h4>
                                                                            {cruce?.items?.length > 0 ? (
                                                                                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white text-sm">
                                                                                    <table className="min-w-full">
                                                                                        <thead className="bg-gray-100">
                                                                                            <tr>
                                                                                                <th className="px-3 py-1.5 text-left text-xs">Concepto</th>
                                                                                                <th className="px-3 py-1.5 text-right text-xs">F29</th>
                                                                                                <th className="px-3 py-1.5 text-right text-xs">Contab.</th>
                                                                                                <th className="px-3 py-1.5 text-center text-xs">OK</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {cruce.items.map((item, i) => (
                                                                                                <tr key={i} className={item.cuadra ? '' : 'bg-orange-50'}>
                                                                                                    <td className="px-3 py-1 text-gray-700">{item.label}</td>
                                                                                                    <td className="px-3 py-1 text-right">{fmtMonto(item.propuesta)}</td>
                                                                                                    <td className="px-3 py-1 text-right">{fmtMonto(item.contabilidad)}</td>
                                                                                                    <td className="px-3 py-1 text-center">{item.cuadra ? '✓' : '✗'}</td>
                                                                                                </tr>
                                                                                            ))}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            ) : (
                                                                                <p className="text-sm text-gray-500 italic py-2 bg-white border border-gray-200 rounded-lg px-3">
                                                                                    {cruce?.resumen || 'Sin datos de cruce para este período.'}
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Terminal sync propuesta</h4>
                                                                            {prop?.errorMensaje && (
                                                                                <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-2">{prop.errorMensaje}</p>
                                                                            )}
                                                                            {renderTerminal(prop?.logEntries, 'Sin logs de sync propuesta.')}
                                                                            {prop?.errorScreenshot && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        const token = localStorage.getItem('token');
                                                                                        window.open(`${api.defaults.baseURL}/f29/descargar-captura-propuesta/${emp._id}/${selectedAnio}/${selectedMes}?token=${token}`, '_blank');
                                                                                    }}
                                                                                    className="mt-2 text-xs text-red-700 underline hover:text-red-900"
                                                                                >
                                                                                    Ver captura del error (propuesta)
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Terminal declaración</h4>
                                                                            {renderTerminal(log?.logEntries, 'Sin logs de declaración para este período.')}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    {empresas.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-16 text-center">
                                                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                                    <FontAwesomeIcon icon={faList} className="text-gray-400 text-xl" />
                                                </div>
                                                <p className="text-gray-500 font-medium">No hay empresas cargadas en la base de datos.</p>
                                                <p className="text-gray-400 text-sm mt-1">Importa un CSV o usa Agregar empresas.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB: HISTORIAL */}
            {activeTab === 'historial' && (
                <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden flex flex-col h-full">
                    <div className="p-5 bg-white border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">Registro Histórico ({MESES[selectedMes]} {selectedAnio})</h3>
                            <p className="text-sm text-gray-500 mt-1">Declaraciones F29 y sync de propuesta SII.</p>
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                            <input
                                type="text"
                                placeholder="Buscar por RUT o Nombre..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="block w-full sm:w-64 pl-3 pr-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                            />
                            <button 
                                onClick={refrescarHistorial} 
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
                            >
                                <FontAwesomeIcon icon={faHistory} className="mr-2" />
                                Refrescar
                            </button>
                        </div>
                    </div>
                    <div className="overflow-x-auto max-h-[700px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 shadow-sm z-10">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Fecha / Hora</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Empresa (RUT)</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Estado</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tipo</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Diagnóstico / Mensaje</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {buildHistorialItems()
                                    .filter(item => !searchTerm || item.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) || item.rut?.includes(searchTerm))
                                    .sort((a, b) => {
                                        const getPriority = item => {
                                            if (item.estado === 'en_progreso') return 1;
                                            if (item.estado === 'en_cola') return 2;
                                            if (item.estado === 'error') return 3;
                                            return 4;
                                        };
                                        const pA = getPriority(a);
                                        const pB = getPriority(b);
                                        if (pA !== pB) return pA - pB;
                                        return (a.nombre || '').localeCompare(b.nombre || '', 'es');
                                    })
                                    .map((item) => (
                                    <React.Fragment key={item._id}>
                                        <tr 
                                            className="hover:bg-blue-50/50 transition-colors cursor-pointer"
                                            onClick={() => setExpandedLogId(expandedLogId === item._id ? null : item._id)}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.updatedAt ? new Date(item.updatedAt).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                                            </td>
                                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                <div className="flex flex-col">
                                                    <span>{item.nombre}</span>
                                                    <span className="text-xs text-gray-500 font-normal">{item.rut}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getEstadoBadge(item)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2.5 py-1 text-xs font-semibold rounded-md border ${
                                                    item.tipo === 'propuesta' ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-gray-50 border-gray-200 text-gray-600'
                                                }`}>
                                                    {item.tipo === 'propuesta' ? 'SYNC PROPUESTA' : (item.resultado?.type || 'declaración').replace(/_/g, ' ').toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600 break-words max-w-md">
                                                <div className={`p-2 rounded-md flex justify-between items-center ${item.estado === 'error' ? 'bg-red-50 text-red-800 border border-red-100' : 'bg-gray-50'}`}>
                                                    <span>{item.resultado?.mensaje || (item.estado === 'en_cola' ? 'Esperando turno...' : 'Sin detalles')}</span>
                                                    <span className="text-xs text-gray-400 ml-2">{expandedLogId === item._id ? '▲' : '▼'}</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedLogId === item._id && (
                                            <tr>
                                                <td colSpan="5" className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                                    {renderTerminal(item.logEntries)}
                                                    {item.propuestaScreenshot && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const token = localStorage.getItem('token');
                                                                window.open(`${api.defaults.baseURL}/f29/descargar-captura-propuesta/${item.empresaId}/${selectedAnio}/${selectedMes}?token=${token}`, '_blank');
                                                            }}
                                                            className="mt-3 bg-red-900/80 hover:bg-red-800 text-red-100 px-3 py-1.5 rounded text-xs font-bold"
                                                        >
                                                            Ver captura del error
                                                        </button>
                                                    )}
                                                    {item.resultado?.errorScreenshot && item.tipo === 'declaracion' && (
                                                        <button 
                                                            onClick={() => {
                                                                const token = localStorage.getItem('token');
                                                                window.open(`${api.defaults.baseURL}/f29/descargar-captura/${item._id}?token=${token}`, '_blank');
                                                            }}
                                                            className="mt-3 bg-red-900/80 hover:bg-red-800 text-red-100 px-3 py-1.5 rounded text-xs font-bold"
                                                        >
                                                            Ver captura del error
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                                {buildHistorialItems().length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-16 text-center">
                                            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                                <FontAwesomeIcon icon={faHistory} className="text-gray-400 text-xl" />
                                            </div>
                                            <p className="text-gray-500 font-medium">No hay registros históricos para este período.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {showAgregarRenta && (
                <AgregarEmpresasRentaModal
                    empresasF29={empresas}
                    onClose={() => setShowAgregarRenta(false)}
                    onAgregadas={cargarEmpresas}
                />
            )}
        </div>
    );
}

export default PanelF29Admin;
